// frontend/src/engine/app/offsite-config.ts
import type { ComponentType } from "react";

/** Props, die jede Slide von der Engine bekommt. (übernommen aus slides/index.ts) */
export interface SlideProps {
  index: number;
  total: number;
  onJump?: (slideNumber: number) => void;
}

/** Ein Schnitt-Marker auf der Presenter-Progress-Bar. */
export interface ProgressCut {
  /** 1-basierte Foliennummer, an der der Marker sitzt. */
  atSlide: number;
  label: string;
  icon: string;
}

/** Bekannte Workshop-Phasen (Reihenfolge ist offsite-spezifisch). */
export type PhaseId =
  | "intro"
  | "topics"
  | "voting"
  | "grouping"
  | "working"
  | "evaluating"
  | "showcase"
  | "evaluated"
  | "overnight"
  | "reveal"
  | "einfuehrung"
  | "abschluss";

/**
 * Der Stations-Ablauf des Strategietags (Briefing Prompt-Teil): acht Schritte
 * mit servergeführten Timern, Rahmenfolien (E1/E2, V1–V3, A1) und Soll-Dauern.
 * Nur gesetzt, wenn die Moderationsleiste diesen Ablauf fahren soll — die
 * AR-Sitzung behält ohne diesen Block ihre bisherige Sechser-Kette.
 */
export interface StationsAblauf {
  /** [erste, letzte] Folie der Einführung (E1, E2) — 0-basiert, inklusiv. */
  einfuehrungSlides: [number, number];
  /** Folie „Die drei Felder erklärt" (Schritt Prompten). */
  promptenSlide: number;
  /** [erste, letzte] Folie der Generierung (Anstoß/Fortschritt). */
  generierungSlides: [number, number];
  /** [erste, letzte] Folie des Vortrags (V1–V3, T1–T3) — Auto-Flow. */
  vortragSlides: [number, number];
  /** Abschlussfolie A1 (Zähler). */
  abschlussSlide: number;
  /** Soll-Dauern in Sekunden — Konfiguration, nicht Code (Briefing 3). */
  phaseDauernSec: {
    einfuehrung: number;
    thema: number;
    prompten: number;
    arbeitsphase: number;
    generierung: number;
    vortrag: number;
    ergebnisse: number;
    abschluss: number;
  };
}

/** Ein Eingabefeld im Anforderungs-Formular. */
export interface RequirementFieldDef {
  /** Schlüssel in RequirementFields — muss dort existieren. */
  key:
    | "thema"
    | "was"
    | "fuerWen"
    | "problem"
    | "anforderungen"
    | "einschraenkungen"
    | "beispiel";
  label: string;
  placeholder: string;
  /** Mehrzeiliges Feld. Default: false. */
  multiline?: boolean;
  /** Wird mit dem Rahmenthema vorbelegt. Höchstens ein Feld pro Offsite. */
  prefillFromFraming?: boolean;
  /**
   * Dauerhaft sichtbarer Erklärtext unter dem Label — kein Tooltip. Für
   * Teilnehmer, die zum ersten Mal mit einer KI arbeiten, ist die Erklärung
   * Teil der Führung und darf nicht hinter einer Interaktion liegen.
   */
  hint?: string;
}

export interface OffsiteConfig {
  id: string;
  displayName: string;
  subdomain: string;
  /** Absolute Basis-URL der zugehörigen API (pro Offsite eigene Subdomain). */
  apiBaseUrl: string;
  deck: ComponentType<SlideProps>[];
  progressCuts: ProgressCut[];
  /**
   * Soll-Endzeit je Folie (1-basiert → "HH:MM"). Steht klein neben
   * „Folie n / m" in der Fortschrittsleiste und wird bei Überziehung dezent
   * orange — ein Zeitplan, den nur der Vortragende liest.
   */
  sollZeiten?: Record<number, string>;
  phases: PhaseId[];
  /** Verweise auf Prompt-Sätze in @offsite/shared — nie der Prompt-Text selbst. */
  ai: { evalPromptKey: string; overnightPromptKey: string };
  /**
   * 0-basierter Slide-Index, auf den beim Start der Overnight-Phase gesprungen
   * wird (Ankündigungs-Folie). Weglassen, wenn das Offsite keine solche Folie
   * hat — dann wird beim Phasenwechsel nicht gesprungen.
   */
  overnightAnnouncementSlide?: number;
  /**
   * 0-basierter Slide-Index für die Warte-Phase. Solange „Warten auf Gäste“
   * läuft, gehört auf die Leinwand, was die Gäste brauchen: Adresse, PIN,
   * QR-Code. Weglassen = kein Sprung, die Leinwand bleibt wo sie ist.
   */
  waitingSlide?: number;
  /**
   * Der Exkurs im Ablauf: Wohin der Moderator wechselt, während die KI baut.
   *
   * Steht in der Moderationsleiste zwischen Generierung und Ergebnissen und ist
   * ein Link — ein neuer Tab, damit die Sitzung im ersten stehen bleibt.
   * Weggelassen: kein Marker.
   */
  exkurs?: { label: string; url: string };
  /**
   * Anzahl der Folien, die frei über die Seitenleiste angefahren werden — die
   * Einführung. Alles danach führt die Moderationsleiste, damit Folie und
   * Ablauf-Schritt nicht auseinanderlaufen können.
   *
   * Ohne Angabe ist das ganze Deck frei navigierbar (Vertriebs-Verhalten).
   */
  introSlides?: number;
  /**
   * 0-basierter Slide-Index der Arbeitsphase. Sobald die Uhr läuft, gehört auf
   * die Leinwand die Zeit — nicht mehr der QR-Code. Ohne Angabe bleibt die
   * Warte-Folie stehen (bisheriges Verhalten).
   */
  workingSlide?: number;
  /**
   * Handys schreiben erst, wenn die Uhr läuft — auch ohne soloTeams. „Anforderung"
   * öffnet dann nur die Vorschau; der Timer-Start gibt die Steckbriefe frei.
   */
  sperreBisUhr?: boolean;
  /** Leinwand-Inhalt der Arbeitsphase vor dem Timer-Start (z. B. die Themen). */
  arbeitsphaseVorschau?: ComponentType;
  /** Vorgewählte Timer-Dauer in Minuten (Standard 15). */
  arbeitsphaseMinuten?: number;
  /**
   * 0-basierter Slide-Index der Themenwahl. Der Ablauf-Schritt „Thema" bringt
   * diese Folie auf die Leinwand — die Wahl fällt vor dem Gremium, nicht in
   * der Seitenleiste des Moderators.
   */
  themaSlide?: number;
  /**
   * Vorbereitete Rahmenthemen: ein Klick statt Tippen vor Publikum. Sie stehen
   * auf der Themen-Folie und im Moderatorpult — dieselbe Liste, damit beide
   * nicht auseinanderlaufen. Ein eigenes Thema lässt sich immer frei eingeben.
   */
  preparedTopics?: { short: string; full: string }[];
  /**
   * Text auf dem Teilnehmer-Handy, während die KI baut. Der Standard spricht
   * von „über Nacht“ — richtig fürs Offsite, falsch in einer Sitzung, in der
   * das Ergebnis zwei Minuten später auf der Leinwand steht.
   */
  overnightWaitLabel?: string;
  /**
   * Felder des Anforderungs-Formulars. Weggelassen = die sechs Vertriebs-Felder
   * (Rückwärtskompatibilität). Die AR-Session setzt hier drei.
   */
  requirementFields?: RequirementFieldDef[];
  /**
   * Ein Teilnehmer = ein Team. Überspringt Voting und Gruppierung; beim Login
   * wird automatisch ein eigenes Team vergeben.
   */
  soloTeams?: boolean;
  /**
   * Feste Teams (netzwirtschaft): Anzahl der Teams. Jedes Team hat einen
   * eigenen mehrfach einlösbaren Code (QR auf der Leinwand) — der Scan führt
   * direkt in den Team-Arbeitsbereich, jedes Mitglied darf editieren. Kein
   * Voting, keine Gruppierung. Erfordert codeLogin.
   */
  festeTeams?: number;
  /**
   * Anzeigenamen der festen Teams (Index 0 = Team-Nummer 1). Für Gruppen,
   * die nicht durchnummeriert heißen — netzwirtschaft: Gruppe 4 teilt sich
   * in 4a/4b/4c, also ["Team 1","Team 2","Team 3","Team 4a","Team 4b",
   * "Team 4c"]. Ohne Angabe heißt Team n überall "Team n".
   */
  festeTeamLabels?: string[];
  /**
   * Themenfindung am ersten Tag (netzwirtschaft, 24.09.2026): Während der
   * Arbeitsphase zeigt die Leinwand statt „Eingereicht x / y" die Uhr und
   * je Team nur, ob das Feld `thema` schon steht — ohne den Text, damit sich
   * die Teams nicht gegenseitig abschreiben.
   *
   * Aktiv nur, solange BEIDES gilt: die Leinwand steht vor `vorFolie`
   * (0-basiert, der Tagescut) und es ist vor `bis` (ISO). Das Datum macht
   * Tag 2 unverwechselbar, auch nach einem Neuladen (Folie steht dann auf 0);
   * die Folienposition hält eine Tag-2-Probe am Vortag im Normalbild.
   * Ohne Angabe: nie (alle anderen Offsites).
   */
  themenfindung?: {
    vorFolie: number;
    bis: string;
  };
  /**
   * Siezen statt duzen. Das Vertriebs-Offsite duzt (Kollegen unter sich), die
   * Gremien-Sitzung siezt (Aufsichtsrat). Betrifft nur die Teilnehmer-Ansicht;
   * Folien und Moderatorpult formulieren ihre Texte ohnehin selbst.
   */
  formal?: boolean;
  /**
   * Kurzzeichen in der Kopfzeile von Handy- und Admin-Ansicht. Ohne Angabe
   * "SVO · OFFSITE 2026" — in einer Gremien-Sitzung falsch, weil die Gäste
   * kein Offsite besuchen.
   */
  wordmark?: string;
  /**
   * Phasen, in denen die Leinwand die Workshop-Oberfläche zeigt statt der
   * Folien. Weggelassen = alle Workshop-Phasen (Vertriebs-Verhalten).
   *
   * Die Gremien-Sitzung setzt hier nur "reveal": In der Arbeitsphase zeigte
   * die Leinwand sonst die Moderator-Bedienung mit Dauer-Auswahl und einem
   * zweiten, konkurrierenden Timer — vor Publikum, geduzt, mit 15 Minuten
   * voreingestellt statt der acht des Ablaufs.
   */
  workshopOverlayPhases?: string[];
  /**
   * Die Ergebnisseite nach dem Offsite: Auswertung und Prototypen zum
   * Anklicken, gebaut mit scripts/build-ergebnisseite.mjs und als statische
   * Dateien deployt. Ist sie gesetzt, führt /ergebnisse dorthin und der
   * Wartebildschirm bietet sie an — das ist der Weg, auf dem Teilnehmer ihre
   * Prototypen nach dem Tag wiederfinden.
   *
   * Die Zieldateien liegen ohne eigene Anmeldung im Bucket; die Route hinter
   * dem Login macht den Pfad nur nicht öffentlich sichtbar.
   */
  ergebnisse?: {
    url: string;
    label: string;
    /**
     * Frühester Zeitpunkt (ISO), ab dem der Wartebildschirm den Link zeigt.
     * Nötig, wenn der Pfad schon vor dem Offsite feststeht (QR auf der
     * Danke-Folie) — sonst stünde der Link auf dem Wartebildschirm, bevor
     * es irgendetwas zu sehen gibt.
     */
    ab?: string;
  };
  copy?: Record<string, string>;
  /**
   * Login per Einmal-Code (6 Zeichen) statt 4-stelliger Gruppen-PIN. Jeder
   * Code funktioniert genau einmal und bindet das Ergebnis an eine Person.
   */
  codeLogin?: boolean;
  /**
   * Pflicht-Identitätsfelder nach dem Login, vor dem Editor. Nur zusammen
   * mit codeLogin sinnvoll — ohne Code gibt es keine Person, an der die
   * Angaben hängen.
   */
  identity?: { nameLabel: string; abteilungLabel: string };
  /**
   * Gestrippte Einzel-Oberfläche: keine Logout-Leiste, kein Team-Wording.
   * Für Sitzungen, in denen Einzelpersonen prompten und jedes Wort auf dem
   * Handy zählt.
   */
  einzelmodus?: boolean;
  /**
   * Anzahl der Bühnen-Plätze. Die Zugangsfolie zeigt damit „Bühne 3/6 ·
   * 12 aus dem Publikum" statt einer Gesamtzahl. Ohne Angabe bleibt die
   * alte Anzeige („4 sind angemeldet").
   */
  buehnenPlaetze?: number;
  /**
   * Gleichstand beim Voting per Auslosung entscheiden: statt des
   * Moderator-Choosers erscheint ein „Per Los entscheiden"-Knopf, der die
   * gleichauf liegenden Themen animiert durchläuft und zufällig eines
   * einfriert. Die manuelle Wahl bleibt als Fallback klickbar.
   */
  tieBreakRandom?: boolean;
  /** Stations-Ablauf mit servergeführten Timern (Strategietag). */
  stationsAblauf?: StationsAblauf;
  /**
   * Sprechernotizen je Folien-Index — erscheinen klein in der Moderations-
   * leiste und im Pult, nie auf der Leinwand (Briefing Prompt-Teil 5).
   */
  sprechernotizen?: Record<number, string>;
}

export function assertOffsiteConfig(c: OffsiteConfig): void {
  if (!c.id) throw new Error("OffsiteConfig: id fehlt");
  if (!c.displayName) throw new Error("OffsiteConfig: displayName fehlt");
  if (!c.subdomain) throw new Error("OffsiteConfig: subdomain fehlt");
  if (!c.apiBaseUrl) throw new Error("OffsiteConfig: apiBaseUrl fehlt");
  if (!Array.isArray(c.deck) || c.deck.length === 0)
    throw new Error("OffsiteConfig: deck ist leer");
  if (!c.ai?.evalPromptKey || !c.ai?.overnightPromptKey)
    throw new Error("OffsiteConfig: ai-Prompt-Keys fehlen");
  if (c.requirementFields && c.requirementFields.length === 0)
    throw new Error("OffsiteConfig: requirementFields ist leer");
  if (
    c.requirementFields &&
    c.requirementFields.filter((f) => f.prefillFromFraming).length > 1
  )
    throw new Error(
      "OffsiteConfig: höchstens ein Feld darf prefillFromFraming setzen",
    );
  if (c.identity && !c.codeLogin)
    throw new Error("OffsiteConfig: identity erfordert codeLogin");
  if (c.festeTeams !== undefined && !c.codeLogin)
    throw new Error("OffsiteConfig: festeTeams erfordert codeLogin");
  if (c.festeTeams !== undefined && (c.festeTeams < 1 || !Number.isInteger(c.festeTeams)))
    throw new Error("OffsiteConfig: festeTeams muss eine positive ganze Zahl sein");
  if (c.festeTeams !== undefined && c.soloTeams)
    throw new Error("OffsiteConfig: festeTeams und soloTeams schließen sich aus");
  if (c.festeTeamLabels && c.festeTeamLabels.length !== (c.festeTeams ?? -1))
    throw new Error(
      "OffsiteConfig: festeTeamLabels muss genau festeTeams Einträge haben",
    );
}
